import { useContext } from 'react';
import * as R from 'ramda';

import DataTypeInput, {
  BooleanOrientation,
} from '@atom/components/common/dataTypeInput/DataTypeInput';
import colors from '@atom/styles/colors';
import { SchemaAttribute } from '@atom/types/schema';
import { removeFromSet } from '@atom/utilities/setUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { MILEPOST_TITLE } from '../assetRouteTab/assetRouteUtilities';
import CreateAssetModalContext from '../CreateAssetModalContext';

import classes from './attributesTab.module.css';

interface Props {
  attribute: SchemaAttribute;
}

const AttributeRow = ({ attribute }: Props) => {
  const {
    schema,
    attributes,
    setAttributes,
    errorAttributeIds,
    setErrorAttributeIds,
  } = useContext(CreateAssetModalContext);

  const onChange = value => {
    setAttributes({
      ...attributes,
      [attribute.id]: { value },
    });

    if (errorAttributeIds.has(attribute.id)) {
      setErrorAttributeIds(removeFromSet(errorAttributeIds, attribute.id));
    }
  };

  const name = attribute.isRequired ? `* ${attribute.name}` : attribute.name;
  const value = isNilOrEmpty(attribute.defaultValue)
    ? R.pathOr(null, [attribute.id, 'value'], attributes)
    : attribute.defaultValue;
  const isMilepostLocked =
    schema?.lrsRequired && attribute.name === MILEPOST_TITLE;
  const isDisabled =
    !attribute.isEditable ||
    !isNilOrEmpty(attribute.defaultValue) ||
    isMilepostLocked;
  const error = errorAttributeIds.has(attribute.id);

  const nameStyle = {
    color: error ? colors.brand.red : colors.neutral.black,
  };

  return (
    <div className={classes.attributeRow}>
      <div className={classes.attributeName} style={nameStyle}>
        {name}
      </div>
      <div className={classes.attributeInput}>
        <DataTypeInput
          id={attribute.id}
          dataType={attribute.dataType}
          options={attribute.enumeration}
          thousandsSeparator={attribute.thousandsSeparator}
          onChange={onChange}
          value={value}
          disabled={isDisabled}
          error={error}
          booleanOrientation={BooleanOrientation.HORIZONTAL}
          unit={attribute?.unit}
          dataCyPrefix={`globalCreateAssetModal_${attribute.name}_`}
        />
      </div>
    </div>
  );
};

export default AttributeRow;
